import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInterceptor';

import {
    Box, Paper, Fab, Typography, CircularProgress, Button, Container
} from '@mui/material';

import {
    DataGrid,
    gridPageCountSelector,
    GridPagination,
    useGridApiContext,
    GridToolbar,
    useGridSelector,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';

// Custom components and functions
import CustomStatusChip from '../../utils/components/CustomStatusChip';
import useWindowDimensions from '../../utils/functions/useWindowDimensions';
import { getDate } from '../../utils/functions/Utils';
import LocaleTextDataGrid from '../../utils/functions/LocaleTextDataGrid';

const TablaSalidas = () => {

    let navigate = useNavigate();
    const auth = useSelector((state) => state.auth);

    const [data, setData] = useState([{}]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState('');
    const { height } = useWindowDimensions();

    useEffect(() => {
        if (!['Admin', 'Vendedor', 'Tecnico', 'Almacen', 'Secretaria', 'Ayudante'].includes(auth.user.rol)) {
            navigate('/dashboard');
        }
        document.title = 'Salidas de almacén';
    }, [auth])

    const getData = async () => {
        setLoading(true);
        try {
            let res;
            if (['Admin', 'Almacen', 'Secretaria', 'Ayudante'].includes(auth.user.rol))
                res = await axios.get('/api/deliveries/');
            else if (['Vendedor'].includes(auth.user.rol)) {
                res = await axios.get('/api/deliveries/')
                res.data = res.data.filter(x => x.elaboradoPor === auth.user.name);
            }
            else
                res = await axios.get(`/api/deliveries/user/${auth.user.name}`);
            if (res.status === 200 && res.data.length > 0) {
                if (res.data.length <= 1) {
                    res.data[0].fechaDeSolicitud = getDate(res.data[0].fechaDeSolicitud, 'DD H:mm:ss');
                    res.data[0].fechaDeEntrega = getDate(res.data[0].fechaDeEntrega, 'DD H:mm:ss');
                    res.data[0].noOrden = res.data[0].operacion === 'Servicio' ? 'Reporte #' + res.data[0].infoServicio.noReporte : res.data[0].noOrden;
                    setData(res.data);
                }
                else {
                    const tmp = res.data.map((item) => {
                        item.fechaDeSolicitud = getDate(item.fechaDeSolicitud, 'DD H:mm:ss');
                        item.fechaDeEntrega = getDate(item.fechaDeEntrega, 'DD H:mm:ss');
                        item.noOrden = item.operacion === 'Servicio' ? 'Reporte #' + item.infoServicio.noReporte : item.noOrden;
                        return item;
                    });
                    setData(tmp);
                }
                setLoading(false);
                document.title = 'Tabla de Salidas';
            } else {
                setData([]);
                setLoading(false);
            }
        } catch (err) {
            console.log(err);
            alert('Ocurrió un error al cargar las salidas, favor de intentarlo mas tarde');
            setLoading(false);
        }
    }

    useEffect(() => {
        getData();
    }, [])

    const renderSalidas = () => {
        if (data.length === 0) return null;
        else {
            return (
                <div>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'start', alignItems: 'center' }}>
                        <Button size='small' variant='outlined' onClick={() => navigate('/inventario')}><i className='fad fa-arrow-left'></i> &nbsp;&nbsp;Regresar</Button>
                        <Typography variant='h5' align='left' sx={{ mt: 1, mb: 1, fontWeight: 700, ml: 3 }}>Salidas de almacén</Typography>
                    </div>
                    <DataGrid
                        autoPageSize={true}
                        sx={{ '& .MuiDataGrid-toolbarContainer': { position: 'fixed', top: '70px', right: '24px' }, '& .MuiButtonBase-root': { color: 'comp.gray' }, '& .MuiDataGrid-row': { backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': { backgroundColor: 'comp.tableHeaderBg', color: 'text.primary' } }}
                        style={{ height: `${height - 120}px`, width: '100%', overflow: 'hidden' }}
                        localeText={LocaleTextDataGrid}
                        rows={data}
                        density='compact'
                        getRowId={(row) => row._id}
                        onCellClick={(e) => { setSelected(e.row._id) }}
                        onCellDoubleClick={(e) => { navigate(`/inventario/salidaProductos/${e.row._id}`, { target: '_blank' }) }}
                        columns={[
                            { field: 'noSolicitud', headerName: 'Solicitud', flex: 1, minWidth: 70, maxWidth: 80, headerAlign: 'center', align: 'center' },
                            { field: 'elaboradoPor', headerName: 'Elaborado por', flex: 1, minWidth: 200, headerAlign: 'center', align: 'center' },
                            { field: 'solicitante', headerName: 'Solicitante', flex: 1, minWidth: 200, headerAlign: 'center', align: 'center' },
                            {
                                field: 'estatus',
                                flex: 1,
                                minWidth: 200,
                                headerName: 'Estatus',
                                headerClassName: 'text-bold',
                                align: 'center',
                                headerAlign: 'center',
                                renderCell: (params) => (
                                    <CustomStatusChip estatus={params.row.estatus} label={params.row.estatus} />
                                ),
                            },
                            { field: 'noOrden', headerName: '# Cotización', align: 'center', headerAlign: 'center', flex: 1, minWidth: 200 },
                            { field: 'entrega', flex: 1, headerName: 'Entrega', align: 'center', headerAlign: 'center', minWidth: 200 },
                            { field: 'recibe', flex: 1, headerName: 'Quien recibe', align: 'center', headerAlign: 'center', minWidth: 200 },
                            { field: 'auth', flex: 1, headerName: 'Autoriza', align: 'center', headerAlign: 'center', minWidth: 200 },
                            {
                                field: 'motivo',
                                flex: 1,
                                headerName: 'Motivo',
                                align: 'center',
                                headerAlign: 'center',
                                maxWidth: 100,
                                minWidth: 100,
                                renderCell: (params) => (
                                    <CustomStatusChip estatus={params.row.motivo} label={params.row.motivo} />
                                ),
                            },
                            { field: 'fechaDeSolicitud', flex: 1, headerName: 'Fecha de solicitud', align: 'center', headerAlign: 'center', minWidth: 200 },
                            { field: 'fechaDeEntrega', flex: 1, headerName: 'Fecha de entrega', align: 'center', headerAlign: 'center', minWidth: 200 },
                        ]}
                        pagination
                        slots={{
                            pagination: CustomPagination,
                            toolbar: GridToolbar
                        }}
                        initialState={{
                            ...data.initialState,
                            pagination: { paginationModel: { pageSize: 25 } },
                        }}
                    />
                </div>
            )
        }
    }

    return (
        <Container maxWidth='xxl' sx={{ width: '100%', overflow: 'hidden', margin: '0px', padding: '4px 4px !important' }}>
            <Paper >
                {loading ?
                    <Box style={{ display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '128px' }}>
                        <CircularProgress size='5em' color='primary' />
                    </Box> :

                    <>
                        {selected !== '' ?
                            <div>
                                <Link target='_blank' to={{ pathname: '/inventario/salidaProductos/' + selected, state: { id: selected } }} style={{ textDecoration: 'none' }}>
                                    <Fab aria-label="add" color='warning' style={{ position: 'fixed', bottom: '8px', left: '8px', zIndex: '10', color: '#FFF' }}>
                                        <i className="fad fa-edit fa-2x"></i>
                                    </Fab>
                                </Link>
                            </div>
                            : null}
                        {renderSalidas()}
                    </>
                }
            </Paper>
        </Container>
    );
}

function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <MuiPagination
            color="primary"
            className={className}
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
                onPageChange(event, newPage - 1);
            }}
        />
    );
}

function CustomPagination(props) {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
}

export default TablaSalidas;